/**
 * @file wgc-monitoring.js
 *
 * Defines window.wgcMonitoring — a global logging shim that other Drupal
 * modules can use to send messages to Grafana Faro without depending on
 * Faro internals.
 *
 * Can be loaded independently of the full Faro stack. When Faro is present
 * (window.wgcFaro is set by faro-telemetry.js), messages are forwarded to
 * Grafana Cloud. When Faro is absent — because the module is disabled,
 * GRAFANA_FARO_URL is unset, or this file is loaded standalone — each method
 * falls back to its native console equivalent so output is never silently lost.
 *
 * Load order relative to faro-telemetry.js does not matter: the Faro instance
 * is resolved lazily at call time via window.wgcFaro.
 *
 * @see js/faro-telemetry.js
 * @see wgc_monitoring.module
 */

(function () {

  /**
   * Returns the initialised Faro instance, or null if unavailable.
   * Resolved lazily so this file can be loaded in any order.
   *
   * @returns {Object|null}
   */
  function getFaro() {
    return window.wgcFaro || null;
  }

  /**
   * Global logging shim.
   *
   * @namespace wgcMonitoring
   */
  window.wgcMonitoring = {

    /**
     * Send a log message to Grafana Faro, or console.log if Faro is unavailable.
     *
     * @param {string} message - The message to log.
     * @param {string} [level='info'] - Severity: 'trace'|'debug'|'info'|'warn'|'error'.
     * @param {Object.<string, string>} [context={}] - Key/value metadata pairs.
     *
     * @example
     * wgcMonitoring.log('Cache miss', 'warn', { node: '42' });
     */
    log: function (message, level, context) {
      var faro = getFaro();
      if (faro) {
        faro.api.pushLog([String(message)], {
          level: level || 'info',
          context: context || {},
        });
      } else {
        console.log(message);
      }
    },

    /**
     * Send a warning to Grafana Faro, or console.warn if Faro is unavailable.
     *
     * @param {string} message - The message to log.
     * @param {Object.<string, string>} [context={}] - Key/value metadata pairs.
     *
     * @example
     * wgcMonitoring.warn('Payment gateway timeout', { orderId: '42' });
     */
    warn: function (message, context) {
      var faro = getFaro();
      if (faro) {
        faro.api.pushLog([String(message)], { level: 'warn', context: context || {} });
      } else {
        console.warn(message);
      }
    },

    /**
     * Send an error to Grafana Faro, or console.error if Faro is unavailable.
     * Error objects are routed through pushError (preserving the stack trace);
     * plain strings are sent as log messages at error level.
     *
     * @param {Error|string} value - The error or message to report.
     * @param {Object.<string, string>} [context={}] - Key/value metadata pairs.
     *
     * @example
     * wgcMonitoring.error(new Error('Checkout failed'));
     * wgcMonitoring.error('API returned 500', { endpoint: '/api/orders' });
     */
    error: function (value, context) {
      var faro = getFaro();
      if (faro) {
        if (value instanceof Error) {
          faro.api.pushError(value, { context: context || {} });
        } else {
          faro.api.pushLog([String(value)], { level: 'error', context: context || {} });
        }
      } else {
        console.error(value);
      }
    },

  };

}());
